import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Search, Loader, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
/**
 * MaunaAddToQueueModal Component
 *
 * Modal for adding items to queue from Mauna app's sources:
 * - Steps
 * - Breaths
 * - Vision Lists
 * - Habit Groups
 * - Time Blocks
 */
export function MaunaAddToQueueModal({ isOpen, onClose, onAdd, currentQueueItems }) {
    const [activeTab, setActiveTab] = useState('step');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(null);
    // Data for each tab
    const [steps, setSteps] = useState([]);
    const [breaths, setBreaths] = useState([]);
    const [visionLists, setVisionLists] = useState([]);
    const [habitGroups, setHabitGroups] = useState([]);
    const [timeBlocks, setTimeBlocks] = useState([]);
    /**
     * Check if an item is already in the queue
     */
    const isItemInQueue = (itemType, itemId) => {
        return currentQueueItems.some(item => item.itemType === itemType && item.itemId === itemId);
    };
    /**
     * Fetch data when modal opens or tab changes
     */
    useEffect(() => {
        if (!isOpen)
            return;
        const fetchItems = async () => {
            try {
                setIsLoading(true);
                const { data: { user } } = await supabase.auth.getUser();
                if (!user)
                    return;
                switch (activeTab) {
                    case 'step': {
                        const { data } = await supabase
                            .from('steps')
                            .select('id, label, duration, color, icon')
                            .eq('user_id', user.id)
                            .eq('completed', false)
                            .order('created_at', { ascending: false });
                        setSteps(data || []);
                        break;
                    }
                    case 'breath': {
                        const { data } = await supabase
                            .from('breaths')
                            .select('id, name, time_estimation_seconds')
                            .eq('user_id', user.id)
                            .order('created_at', { ascending: false });
                        setBreaths(data || []);
                        break;
                    }
                    case 'vision_list': {
                        const { data } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency')
                            .eq('user_id', user.id)
                            .eq('type', 'vision_list')
                            .order('created_at', { ascending: false });
                        setVisionLists(data || []);
                        break;
                    }
                    case 'habit_group': {
                        const { data } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency')
                            .eq('user_id', user.id)
                            .eq('type', 'habit_group')
                            .order('created_at', { ascending: false });
                        setHabitGroups(data || []);
                        break;
                    }
                    case 'time_block': {
                        const { data } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency')
                            .eq('user_id', user.id)
                            .eq('type', 'time_block')
                            .order('created_at', { ascending: false });
                        setTimeBlocks(data || []);
                        break;
                    }
                }
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchItems();
    }, [isOpen, activeTab]);
    const handleAddItem = async (itemId) => {
        setIsAdding(itemId);
        try {
            // Map tab names to proper ItemType
            const itemType = activeTab === 'step' ? 'step' :
                activeTab === 'breath' ? 'breath' :
                    'length'; // vision_list, habit_group, time_block all use 'length' type
            await onAdd(itemType, itemId);
        }
        finally {
            setIsAdding(null);
        }
    };
    if (!isOpen)
        return null;
    // Filter items based on search
    let items = [];
    let getItemName = () => '';
    let getItemDuration = () => '';
    let getItemSubtitle = () => '';
    switch (activeTab) {
        case 'step':
            items = steps;
            getItemName = (item) => item.label;
            getItemDuration = (item) => {
                const mins = Math.floor(item.duration / 60);
                const secs = item.duration % 60;
                return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
            };
            break;
        case 'breath':
            items = breaths;
            getItemName = (item) => item.name;
            getItemDuration = (item) => {
                const mins = Math.floor(item.time_estimation_seconds / 60);
                const secs = item.time_estimation_seconds % 60;
                return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
            };
            break;
        case 'vision_list':
            items = visionLists;
            getItemName = (item) => item.name;
            getItemDuration = (item) => item.duration ? `${item.duration}m` : '—';
            break;
        case 'habit_group':
            items = habitGroups;
            getItemName = (item) => item.name;
            getItemDuration = (item) => item.duration ? `${item.duration}m` : '—';
            getItemSubtitle = (item) => item.frequency ? `${item.frequency}` : '';
            break;
        case 'time_block':
            items = timeBlocks;
            getItemName = (item) => item.name;
            getItemDuration = (item) => item.duration ? `${item.duration}m` : '—';
            break;
    }
    const filteredItems = items.filter(item => getItemName(item).toLowerCase().includes(searchQuery.toLowerCase()));
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]", children: _jsxs("div", { className: "rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("div", { className: "px-6 py-4 border-b flex items-center justify-between", style: { borderColor: 'var(--border-color)' }, children: [_jsx("h2", { className: "text-lg font-bold", style: { color: 'var(--text-primary)' }, children: "Add to Queue" }), _jsx("button", { onClick: onClose, className: "p-1 rounded transition-colors hover:bg-opacity-50", style: {
                                backgroundColor: 'var(--bg-base)',
                                color: 'var(--text-secondary)'
                            }, children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsx("div", { className: "flex border-b overflow-x-auto min-h-[3rem] shrink-0", style: { borderColor: 'var(--border-color)' }, children: ['step', 'breath', 'vision_list', 'habit_group', 'time_block'].map(tab => {
                        const labels = {
                            'step': 'Steps',
                            'breath': 'Breaths',
                            'vision_list': 'Vision Lists',
                            'habit_group': 'Habit Groups',
                            'time_block': 'Time Blocks'
                        };
                        const isActive = activeTab === tab;
                        return (_jsx("button", { onClick: () => setActiveTab(tab), className: `px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${isActive ? '' : ''}`, style: {
                                borderColor: isActive ? '#3B82F6' : 'transparent',
                                color: isActive ? '#3B82F6' : 'var(--text-secondary)',
                                backgroundColor: isActive ? 'var(--bg-base)' : 'transparent'
                            }, children: labels[tab] }, tab));
                    }) }), _jsx("div", { className: "px-6 py-3 border-b", style: { borderColor: 'var(--border-color)' }, children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-3 w-4 h-4", style: { color: 'var(--text-tertiary)' } }), _jsx("input", { type: "text", placeholder: "Search...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-9 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                } })] }) }), _jsx("div", { className: "flex-1 overflow-y-auto", children: isLoading ? (_jsx("div", { className: "flex items-center justify-center h-40", children: _jsx(Loader, { className: "w-6 h-6 text-blue-500 animate-spin" }) })) : filteredItems.length === 0 ? (_jsx("div", { className: "flex items-center justify-center h-40", style: { color: 'var(--text-secondary)' }, children: _jsxs("p", { children: ["No ", activeTab, "s found"] }) })) : (_jsx("div", { className: "space-y-1 p-2", children: filteredItems.map(item => {
                            const itemType = activeTab === 'step' ? 'step' : activeTab === 'breath' ? 'breath' : 'length';
                            const isQueued = isItemInQueue(itemType, item.id);
                            return (_jsxs("div", { className: "flex items-center justify-between p-3 rounded-lg transition-colors group", style: {
                                    backgroundColor: 'var(--bg-base)',
                                }, children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h4", { className: "font-medium truncate", style: { color: 'var(--text-primary)' }, children: getItemName(item) }), _jsxs("div", { className: "text-xs mt-1 space-y-0.5", style: { color: 'var(--text-tertiary)' }, children: [getItemDuration(item) !== '—' && (_jsxs("p", { children: ["Duration: ", getItemDuration(item)] })), getItemSubtitle(item) && (_jsxs("p", { children: ["Frequency: ", getItemSubtitle(item)] }))] })] }), _jsx("button", { onClick: () => handleAddItem(item.id), disabled: isAdding === item.id || isQueued, className: "ml-2 px-3 py-1 text-white text-sm rounded transition-colors flex-shrink-0 flex items-center gap-1", style: {
                                            backgroundColor: isQueued ? '#9CA3AF' : '#3B82F6',
                                            cursor: isQueued ? 'default' : 'pointer',
                                            opacity: isQueued ? 0.6 : 1
                                        }, children: isQueued ? (_jsxs(_Fragment, { children: [_jsx("span", { children: "\u2713" }), _jsx("span", { children: "Added" })] })) : isAdding === item.id ? (_jsx(Loader, { className: "w-4 h-4 animate-spin" })) : (_jsxs(_Fragment, { children: [_jsx(Plus, { className: "w-4 h-4" }), _jsx("span", { children: "Add" })] })) })] }, item.id));
                        }) })) })] }) }));
}
